// @givmo/app-charity — the getting-started strip's dismissal, persisted per user and organization.
//
// The strip above the app grid (`getting-started-strip.tsx`) lists the setup steps an organization
// still owes. Dismissing it is a device-local preference — the same posture as the active-org hint
// (`org/active-org-storage.ts`): a HINT, never a source of truth, keyed per Firebase uid so two
// accounts on one browser never inherit each other's choice, and per organization so an admin of
// several does not silence the strip for all of them at once. The steps themselves keep coming from
// their reads; a dismissed strip that later gains a new step stays dismissed, which is the honest
// reading of "I said not now".
//
// SSR / privacy-mode guarded exactly like the active-org store: every access no-ops (never throws)
// when `window`/`localStorage` is unavailable.

const KEY_PREFIX = 'givmoCharity.gettingStartedDismissed';

function dismissalKey(uid: string | null, charityId: string): string {
  return `${KEY_PREFIX}.${uid ?? 'anon'}.${charityId}`;
}

function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Whether the strip was dismissed on this device for `uid` + `charityId`. `false` when unavailable. */
export function isGettingStartedDismissed(uid: string | null, charityId: string): boolean {
  const store = storage();
  if (store === null) return false;
  try {
    return store.getItem(dismissalKey(uid, charityId)) === 'true';
  } catch {
    return false;
  }
}

/** Persist the dismissal. No-op (never throws) when storage is unavailable. */
export function dismissGettingStarted(uid: string | null, charityId: string): void {
  const store = storage();
  if (store === null) return;
  try {
    store.setItem(dismissalKey(uid, charityId), 'true');
  } catch {
    // Privacy mode / quota — best-effort; the strip simply comes back on the next load.
  }
}
